// features/workstation-journey-map.js — JOURNEY MAP PANEL
// ═══════════════════════════════════════════════════════════
// Renders the workspace_nodes tree as a top-down mindmap. Auto-layout:
// each new node appears as a child of the most recent active node,
// arranged vertically with horizontal siblings.
//
// Phase 2 capabilities (tier 1 of the dynamic mindmap spec):
//   - Root node auto-created from the workspace goal
//   - Moment nodes appear automatically via Haiku classifier
//   - File nodes appear automatically when artifacts are added
//   - Color per node type (Question sage, Decision gold, Insight purple,
//     Milestone teal, Root gold, File neutral or gold-tinted)
//   - Circle nodes for moments, rounded-rectangle nodes for files
//   - Click-to-navigate: click a node → chat scrolls to its message
//     (canvas state rewind deferred to Phase 5)
//   - Two states: collapsed (narrow rail on the right) and expanded
//     (~33% of the workstation width)
//
// Deferred (Phase 3+):
//   - Cross-reference links (dotted lines between branches)
//   - Branch folding
//   - Live Shift reorganization
//   - Search, zoom, timeline toggle
//   - Drag-to-create manual nodes
// ═══════════════════════════════════════════════════════════

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { NODE_TYPES, NODE_COLORS, FILE_NODE_COLORS } from '../lib/workspace-config';
import { buildLabels, computeRadialLayout } from '../components/journey-surface/journey-layout';
import { LegendGroup } from '../components/journey-surface/journey-legend';
import { JourneyCollapsedRail } from '../components/journey-surface/journey-collapsed-rail';
import { JourneySvgCanvas } from '../components/journey-surface/journey-svg-canvas';
import { JourneyTimeline } from '../components/journey-surface/journey-timeline';
import { JourneyHeader } from '../components/journey-surface/journey-header';

// ── Main component ─────────────────────────────────────────
export default function WorkstationJourneyMap({
  nodes,
  loading,
  expanded,
  thinking,
  focusedNodeId,
  onToggle,
  onNodeClick,
  onDeleteNode,
  supabase,
  workspaceId,
}) {
  // Radial layout — computes the logical tree from type+order (ignoring
  // stored parent_id) and places nodes around the root at angles. The
  // viewBox is square and dynamically sized to fit the deepest branch.
  const { positioned, viewSize } = useMemo(() => computeRadialLayout(nodes), [nodes]);
  const viewWidth = viewSize;
  const viewHeight = viewSize;

  // Build edges from the LOGICAL tree (positioned nodes have rewritten
  // parent_ids from buildLogicalTree). Old workspaces with chronological
  // chains will render correctly because the renderer doesn't trust the
  // DB parent_ids.
  const edges = useMemo(() => {
    const out = [];
    positioned.forEach(node => {
      if (node.parent_id) {
        const parent = positioned.find(n => n.id === node.parent_id);
        if (parent) out.push({ from: parent, to: node });
      }
    });
    return out;
  }, [positioned]);

  // Short labels (M1, Q1, F1...) keyed by node id, derived from creation order
  const labels = useMemo(() => buildLabels(nodes), [nodes]);

  // Hovered file node (for the preview tooltip in expanded mode)
  const [hoveredFile, setHoveredFile] = useState(null);

  // ── Lens picker state ──
  // Set of currently active lens ids. The default tree view always
  // renders; lenses stack on top of it as visual modifiers. See
  // docs/JOURNEY_MAP_INTELLIGENCE.md for the design rationale.
  // Each lens implementation lands as its own workstream — for now
  // toggling a lens shows that it's "armed but unbuilt" so the
  // architecture is visible to the user.
  const [activeLenses, setActiveLenses] = useState(() => new Set());
  const [hoveredLens, setHoveredLens] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapOpen, setMapOpen] = useState(true);
  const searchInputRef = useRef(null);
  const searchWrapperRef = useRef(null);

  // Click-outside to close search
  useEffect(() => {
    if (!searchOpen) return;
    function onClickOutside(e) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setSearchOpen(false);
        if (!searchQuery) setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [searchOpen, searchQuery]);

  function toggleLens(id) {
    setActiveLenses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ── Live status bar state ──
  const [statusText, setStatusText] = useState('Ready');
  const [statusBuilding, setStatusBuilding] = useState(false);

  useEffect(() => {
    function onBuilding(e) {
      const { type, done } = e.detail || {};
      if (done) {
        setStatusBuilding(false);
        setStatusText('Ready');
      } else if (type) {
        setStatusBuilding(true);
        setStatusText(`Building ${type} artifact...`);
      }
    }
    function onArtifactLoaded(e) {
      const { artifact } = e.detail || {};
      if (artifact?.title) {
        setStatusText(`${artifact.title}`);
        setStatusBuilding(false);
      }
    }
    window.addEventListener('workstation:canvas-building', onBuilding);
    window.addEventListener('workstation:artifact-loaded', onArtifactLoaded);
    return () => {
      window.removeEventListener('workstation:canvas-building', onBuilding);
      window.removeEventListener('workstation:artifact-loaded', onArtifactLoaded);
    };
  }, []);

  // Update status when focus changes
  useEffect(() => {
    if (focusedNodeId) {
      const node = (nodes || []).find(n => n.id === focusedNodeId);
      if (node) setStatusText(`${labels[node.id] || ''} open — ${node.title || 'Untitled'}`);
    }
  }, [focusedNodeId, nodes, labels]);

  // Expose nodes to chat panel for chat_reference detection
  useEffect(() => {
    window.__shiftWorkspaceNodes = nodes || [];
    return () => { window.__shiftWorkspaceNodes = []; };
  }, [nodes]);

  // ── Supabase Realtime subscription ──
  // Listen for broadcasts from chat + canvas panels
  useEffect(() => {
    if (!supabase || !workspaceId) return;
    const channel = supabase.channel(`journey:${workspaceId}`);

    channel.on('broadcast', { event: 'node_created' }, ({ payload }) => {
      if (payload?.name) {
        setStatusBuilding(true);
        setStatusText(`Building — ${payload.name}`);
      }
    });

    channel.on('broadcast', { event: 'node_saved' }, ({ payload }) => {
      if (payload?.name) {
        setStatusBuilding(false);
        setStatusText(`${payload.name} saved`);
      }
    });

    channel.on('broadcast', { event: 'canvas_focus' }, ({ payload }) => {
      if (payload?.name) {
        setStatusText(`${payload.name} — open in canvas`);
      }
    });

    channel.on('broadcast', { event: 'chat_reference' }, ({ payload }) => {
      if (payload?.nodeId) {
        setStatusText(`Referenced in chat — ${payload.timeAgo || 'just now'}`);
      }
    });

    channel.subscribe();
    return () => { try { supabase.removeChannel(channel); } catch (_) { /* cleanup */ } };
  }, [supabase, workspaceId]);

  // Filter nodes by search query
  const filteredNodes = useMemo(() => {
    if (!searchQuery.trim()) return nodes || [];
    const q = searchQuery.toLowerCase();
    return (nodes || []).filter(n =>
      (n.title || '').toLowerCase().includes(q) ||
      (n.type || '').toLowerCase().includes(q)
    );
  }, [nodes, searchQuery]);

  // Legend rows in label order (R first, then by appearance)
  const legendRows = useMemo(() => {
    const ordered = [...(searchQuery ? filteredNodes : (nodes || []))].sort((a, b) => {
      if (a.type === 'root') return -1;
      if (b.type === 'root') return 1;
      const at = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
      return at - bt;
    });
    return ordered.map(n => {
      const isFile = n.type === NODE_TYPES.FILE;
      const color = isFile
        ? (FILE_NODE_COLORS[n.file_source] || FILE_NODE_COLORS.uploaded)
        : (NODE_COLORS[n.type] || '#888');
      return {
        id: n.id,
        label: labels[n.id] || '',
        title: n.title,
        type: n.type,
        color,
      };
    });
  }, [nodes, labels]);

  // Collapsed state: narrow rail with expand button
  if (!expanded) {
    return <JourneyCollapsedRail thinking={thinking} nodes={nodes} onToggle={onToggle} />;
  }

  // Expanded state: full panel
  return (
    <div
      style={{
        width: '100%',
        minWidth: 0,
        background: '#07070a',
        borderLeft: thinking ? '1px solid #c8a96e' : '1px solid #1e1e2a',
        boxShadow: thinking ? 'inset 0 0 14px rgba(200,169,110,0.18)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: thinking ? 'wsmap-pulse 1.2s ease-in-out' : 'none',
        transition: 'border-color 0.3s, box-shadow 0.3s',
      }}
    >
      <style jsx global>{`
        @keyframes wsmap-pulse {
          0%, 100% { box-shadow: inset 0 0 4px rgba(200,169,110,0.1); }
          50% { box-shadow: inset 0 0 14px rgba(200,169,110,0.32); }
        }
        @keyframes wsmap-node-in {
          from { opacity: 0; transform: scale(0.6); }
          to { opacity: 1; transform: scale(1); }
        }
        .wsmap-node {
          animation: wsmap-node-in 320ms ease-out backwards;
          transform-box: fill-box;
          transform-origin: center;
        }
      `}</style>

      <JourneyHeader
        nodes={nodes}
        activeLenses={activeLenses}
        hoveredLens={hoveredLens}
        setHoveredLens={setHoveredLens}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchInputRef={searchInputRef}
        searchWrapperRef={searchWrapperRef}
        mapOpen={mapOpen}
        setMapOpen={setMapOpen}
        focusedNodeId={focusedNodeId}
        onToggle={onToggle}
        toggleLens={toggleLens}
      />

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <div style={{ color: '#555', fontSize: 11, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 40 }}>
            Loading map…
          </div>
        )}
        {!loading && positioned.length === 0 && (
          <div style={{ color: '#444', fontSize: 11, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 40, padding: '0 20px', lineHeight: 1.6 }}>
            Your journey map starts here. Nodes will appear as Shift surfaces decisions, questions, and insights during the session.
          </div>
        )}
        {!loading && positioned.length > 0 && (
          <>
            <JourneySvgCanvas
              positioned={positioned}
              edges={edges}
              labels={labels}
              activeLenses={activeLenses}
              focusedNodeId={focusedNodeId}
              hoveredFile={hoveredFile}
              setHoveredFile={setHoveredFile}
              onNodeClick={onNodeClick}
              viewWidth={viewWidth}
              viewHeight={viewHeight}
              mapOpen={mapOpen}
            />

            {/* Node list — grouped by state */}
            <div style={{ borderTop: '1px solid #1e1e2a', padding: '10px 10px' }}>
              {legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && (n.status === 'active' || n.status === 'building' || n.id === focusedNodeId); }).length > 0 && (
                <LegendGroup
                  label="Active" color="#c8a96e"
                  rows={legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && (n.status === 'active' || n.status === 'building' || n.id === focusedNodeId); })}
                  nodes={nodes} positioned={positioned} focusedNodeId={focusedNodeId} onNodeClick={onNodeClick} onDeleteNode={onDeleteNode}
                />
              )}
              {legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status === 'referenced' && n.id !== focusedNodeId; }).length > 0 && (
                <LegendGroup
                  label="Referenced" color="#9b8fc4"
                  rows={legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status === 'referenced' && n.id !== focusedNodeId; })}
                  nodes={nodes} positioned={positioned} focusedNodeId={focusedNodeId} onNodeClick={onNodeClick} onDeleteNode={onDeleteNode}
                />
              )}
              {legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status === 'related' && n.id !== focusedNodeId; }).length > 0 && (
                <LegendGroup
                  label="Related" color="#7a9e82"
                  rows={legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status === 'related' && n.id !== focusedNodeId; })}
                  nodes={nodes} positioned={positioned} focusedNodeId={focusedNodeId} onNodeClick={onNodeClick} onDeleteNode={onDeleteNode}
                />
              )}
              {legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status === 'error'; }).length > 0 && (
                <LegendGroup
                  label="Error" color="#c46050"
                  rows={legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status === 'error'; })}
                  nodes={nodes} positioned={positioned} focusedNodeId={focusedNodeId} onNodeClick={onNodeClick} onDeleteNode={onDeleteNode}
                />
              )}
              <LegendGroup
                label="All nodes" color={null}
                rows={legendRows.filter(r => { const n = (nodes || []).find(nd => nd.id === r.id); return n && n.status !== 'active' && n.status !== 'building' && n.status !== 'referenced' && n.status !== 'related' && n.status !== 'error' && n.id !== focusedNodeId; })}
                nodes={nodes} positioned={positioned} focusedNodeId={focusedNodeId} onNodeClick={onNodeClick} onDeleteNode={onDeleteNode} count={legendRows.length}
              />
            </div>
          </>
        )}
      </div>

      {/* Timeline strip — pinned at bottom */}
      {!loading && (
        <JourneyTimeline nodes={nodes} labels={labels} focusedNodeId={focusedNodeId} onNodeClick={onNodeClick} />
      )}
    </div>
  );
}
